export const BUSINESS_TIME_ZONE = 'America/New_York';

const datePartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
});

const dateTimePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

function partsObject(formatter, value) {
  return Object.fromEntries(formatter.formatToParts(value).filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
}

export function businessDate(value = new Date()) {
  const p = partsObject(datePartsFormatter, value);
  return `${p.year}-${p.month}-${p.day}`;
}

export function businessDateTimeToDate(dateString, hour = 0, minute = 0, second = 0) {
  const [year, month, day] = dateString.split('-').map(Number);
  const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let candidate = new Date(desiredAsUtc);

  // Resolve the America/New_York UTC offset for this specific date, including DST.
  for (let i = 0; i < 3; i += 1) {
    const p = partsObject(dateTimePartsFormatter, candidate);
    const representedAsUtc = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour), Number(p.minute), Number(p.second),
    );
    const correction = desiredAsUtc - representedAsUtc;
    if (correction === 0) break;
    candidate = new Date(candidate.getTime() + correction);
  }
  return candidate;
}

export function businessDateTimeToIso(dateString, hour = 12, minute = 0, second = 0) {
  return businessDateTimeToDate(dateString, hour, minute, second).toISOString();
}

export function addBusinessDays(dateString, amount) {
  const [year, month, day] = dateString.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day + amount, 12));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function moveBusinessMonth(dateString, amount) {
  const [year, month, day] = dateString.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1 + amount, Math.min(day, 28), 12));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function businessDayBounds(dateString = businessDate()) {
  return {
    start: businessDateTimeToDate(dateString, 0),
    end: businessDateTimeToDate(addBusinessDays(dateString, 1), 0),
  };
}

export function businessRangeBounds(mode, anchor, customStart, customEnd) {
  if (mode === 'day') return businessDayBounds(anchor);

  if (mode === 'week') {
    const [year, month, day] = anchor.split('-').map(Number);
    const noonUtc = new Date(Date.UTC(year, month - 1, day, 12));
    const mondayOffset = (noonUtc.getUTCDay() + 6) % 7;
    const startDate = addBusinessDays(anchor, -mondayOffset);
    return { start: businessDateTimeToDate(startDate, 0), end: businessDateTimeToDate(addBusinessDays(startDate, 7), 0) };
  }

  if (mode === 'month') {
    const [year, month] = anchor.split('-').map(Number);
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const nextMonth = new Date(Date.UTC(year, month, 1, 12));
    const endDate = `${nextMonth.getUTCFullYear()}-${String(nextMonth.getUTCMonth() + 1).padStart(2, '0')}-01`;
    return { start: businessDateTimeToDate(startDate, 0), end: businessDateTimeToDate(endDate, 0) };
  }

  return { start: businessDateTimeToDate(customStart, 0), end: businessDateTimeToDate(addBusinessDays(customEnd, 1), 0) };
}

export function moveBusinessAnchor(anchor, mode, delta) {
  if (mode === 'day') return addBusinessDays(anchor, delta);
  if (mode === 'week') return addBusinessDays(anchor, delta * 7);
  if (mode === 'month') return moveBusinessMonth(anchor, delta);
  return anchor;
}

export function formatBusinessDate(value) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(value instanceof Date ? value : new Date(value));
}

export function formatBusinessMonth(value) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    month: 'long', year: 'numeric',
  }).format(value instanceof Date ? value : new Date(value));
}

export function formatBusinessDateTime(value) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(value));
}
